import { action, makeObservable, observable } from 'mobx'

import { DTO } from '../DTO'
import type { Student } from './Student'

export type StudentId = res.StudentAppSetting['studentId']

export type AppSettingKind = keyof Pick<
  StudentAppSetting,
  | '채점전_정답공개'
  | '채점후_정답해설공개'
  | '채점전_문풀동공개'
  | '채점후_문풀동공개'
  | '학부모_공개'
  | '개념힌트_사용'
  | '풀이분석_사용'
  | '풀이확인_사용'
>

export class StudentAppSetting extends DTO {
  studentId: StudentId
  name: Student['name']

  private _showAnswerManage: {
    _showSolutionBeforeScoring: boolean
    _showSolutionAfterScoring: boolean
    _showProblemVideoBeforeScoring: boolean
    _showProblemVideoAfterScoring: boolean
    _showSolutionToParent: boolean
  }

  private _aiTutorManage: {
    _useAiForHintConceptual: boolean
    _useAiForReviewTheSolution: boolean
    _useAiForSolveTheStuckUp: boolean
  }

  get 채점전_정답공개() {
    return this._showAnswerManage._showSolutionBeforeScoring
  }

  set 채점전_정답공개(value: boolean) {
    this._showAnswerManage._showSolutionBeforeScoring = value
  }

  get 채점후_정답해설공개() {
    return this._showAnswerManage._showSolutionAfterScoring
  }

  set 채점후_정답해설공개(value: boolean) {
    this._showAnswerManage._showSolutionAfterScoring = value
  }

  get 채점전_문풀동공개() {
    return this._showAnswerManage._showProblemVideoBeforeScoring
  }

  set 채점전_문풀동공개(value: boolean) {
    this._showAnswerManage._showProblemVideoBeforeScoring = value
  }

  get 채점후_문풀동공개() {
    return this._showAnswerManage._showProblemVideoAfterScoring
  }

  set 채점후_문풀동공개(value: boolean) {
    this._showAnswerManage._showProblemVideoAfterScoring = value
  }

  get 학부모_공개() {
    return this._showAnswerManage._showSolutionToParent
  }

  set 학부모_공개(value: boolean) {
    this._showAnswerManage._showSolutionToParent = value
  }

  get 개념힌트_사용() {
    return this._aiTutorManage._useAiForHintConceptual
  }

  set 개념힌트_사용(value: boolean) {
    this._aiTutorManage._useAiForHintConceptual = value
  }

  get 풀이분석_사용() {
    return this._aiTutorManage._useAiForSolveTheStuckUp
  }

  set 풀이분석_사용(value: boolean) {
    this._aiTutorManage._useAiForSolveTheStuckUp = value
  }

  get 풀이확인_사용() {
    return this._aiTutorManage._useAiForReviewTheSolution
  }

  set 풀이확인_사용(value: boolean) {
    this._aiTutorManage._useAiForReviewTheSolution = value
  }

  constructor(data: res.StudentAppSetting & Pick<Student, 'name'>) {
    super()

    makeObservable<this, '_showAnswerManage' | '_aiTutorManage'>(this, {
      _showAnswerManage: observable,
      _aiTutorManage: observable,
      toggleShowSolutionBeforeScoring: action,
      toggleShowSolutionAfterScoring: action,
      toggleShowProblemVideoAfterScoring: action,
      toggleShowProblemVideoBeforeScoring: action,
      toggleShowSolutionToParent: action,
    })

    this.studentId = data.studentId
    this.name = data.name
    this._showAnswerManage = {
      _showSolutionBeforeScoring: data.showSolutionBeforeScoring,
      _showSolutionAfterScoring: data.showSolutionAfterScoring,
      _showProblemVideoBeforeScoring: data.showProblemVideoBeforeScoring,
      _showProblemVideoAfterScoring: data.showProblemVideoAfterScoring,
      _showSolutionToParent: data.showSolutionToParent,
    }
    this._aiTutorManage = {
      _useAiForHintConceptual: data.useAiForHintConceptual,
      _useAiForReviewTheSolution: data.useAiForReviewTheSolution,
      _useAiForSolveTheStuckUp: data.useAiForSolveTheStuckUp,
    }
  }

  // 정답 및 영상 공개 설정 관련
  toggleShowSolutionBeforeScoring() {
    this._showAnswerManage._showSolutionBeforeScoring =
      !this._showAnswerManage._showSolutionBeforeScoring
  }

  toggleShowSolutionAfterScoring() {
    this._showAnswerManage._showSolutionAfterScoring =
      !this._showAnswerManage._showSolutionAfterScoring
  }

  toggleShowProblemVideoBeforeScoring() {
    this._showAnswerManage._showProblemVideoBeforeScoring =
      !this._showAnswerManage._showProblemVideoBeforeScoring
  }

  toggleShowProblemVideoAfterScoring() {
    this._showAnswerManage._showProblemVideoAfterScoring =
      !this._showAnswerManage._showProblemVideoAfterScoring
  }

  toggleShowSolutionToParent() {
    this._showAnswerManage._showSolutionToParent = !this._showAnswerManage._showSolutionToParent
  }

  // AI 튜터 관련
  toggleUseAiForHintConceptual() {
    this._aiTutorManage._useAiForHintConceptual = !this._aiTutorManage._useAiForHintConceptual
  }

  toggleUseAiForSolveTheStuckUp() {
    this._aiTutorManage._useAiForSolveTheStuckUp = !this._aiTutorManage._useAiForSolveTheStuckUp
  }

  toggleUseAiForReviewTheSolution() {
    this._aiTutorManage._useAiForReviewTheSolution = !this._aiTutorManage._useAiForReviewTheSolution
  }
}
